import { ApiError } from '@hq/api-sdk';
import { formatDisplayDate } from '@hq/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Card, Col, Row, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { hq } from '../session';

/** 统计口径：列表接口不返回 total，这里按单页 200 条上限统计，超出显示 200+。 */
const STAT_SIZE = 200;

interface QualificationRow {
  key: string;
  name: string;
  expireAt: string;
  daysLeft: number | null;
}

const columns: ColumnsType<QualificationRow> = [
  { title: '资质', dataIndex: 'name' },
  { title: '到期日', dataIndex: 'expireAt', width: 140, render: (value: string) => formatDisplayDate(value) },
  {
    title: '剩余',
    dataIndex: 'daysLeft',
    width: 120,
    align: 'right',
    render: (value: number | null) => {
      if (value === null) return '-';
      if (value < 0) return <Tag color="red">已过期 {Math.abs(value)} 天</Tag>;
      return value <= 15 ? <Tag color="red">{value} 天</Tag> : <Tag color="green">{value} 天</Tag>;
    }
  }
];

const cap = (count: number, size: number): string => (count >= size ? `${size}+` : String(count));

export default function Dashboard() {
  // 供应商主体由后端按令牌绑定（`sys_user.supplier_id` → 令牌 sid）解析。
  // 不能用 profile.orgId 近似：机构 id 与 wl_supplier.id 不是同一主键空间，
  // 换一家供应商就会取到别人的档案与资质（docs/00 DR-8.14）。
  const supplier = useQuery({
    queryKey: ['supplier', 'dashboard', 'supplier'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.supplierMe()
  });

  const supplierId = supplier.data?.supplier?.id ?? '';

  const prices = useQuery({
    queryKey: ['supplier', 'dashboard', 'prices', supplierId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.prices({ supplierId }),
    enabled: Boolean(supplierId)
  });

  // 采购单/对账单均由服务端按绑定供应商过滤（供应商门户强制 supplierId，不接受客户端指定）
  const pendingOrders = useQuery({
    queryKey: ['supplier', 'dashboard', 'orders'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.procurement.purchases.list({ size: STAT_SIZE, orderStatus: 'SENT' })
  });

  const pendingSettle = useQuery({
    queryKey: ['supplier', 'dashboard', 'settle'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.list({ size: STAT_SIZE, payeeOnly: true, settleStatus: 'SENT' })
  });

  const quals: QualificationRow[] = (supplier.data?.quals ?? []).map((row) => ({
    key: row.id,
    name: row.qualType ?? row.qualNo ?? '资质',
    expireAt: row.expireAt ?? '',
    daysLeft: row.expireAt ? dayjs(row.expireAt).startOf('day').diff(dayjs().startOf('day'), 'day') : null
  }));

  const skuCount = new Set(
    (prices.data ?? []).filter((row) => row.approvalStatus === 'APPROVED').map((row) => row.materialId)
  ).size;

  const error =
    (supplier.error instanceof ApiError && supplier.error.friendlyMessage) ||
    (prices.error instanceof ApiError && prices.error.friendlyMessage) ||
    null;

  return (
    <>
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message="企业数据加载失败" description={error} /> : null}

      <Card
        className="page-card"
        title={
          <Space>
            企业概览
            <Typography.Text type="secondary">
              统计口径：最近 {STAT_SIZE} 条（超出显示 {STAT_SIZE}+）
            </Typography.Text>
          </Space>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} xl={6}>
            <Statistic title="在售 SKU" value={cap(skuCount, STAT_SIZE)} suffix="个" loading={Boolean(supplierId) && prices.isPending} />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic
              title="待接单"
              value={cap(pendingOrders.data?.items.length ?? 0, STAT_SIZE)}
              suffix="单"
              valueStyle={{ color: '#d46b08' }}
              loading={pendingOrders.isPending}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Tooltip title="发货时效由院内部署的配送接口回传，供应商侧暂无独立统计端点">
              <Statistic title="逾期未发货" value="—" />
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic
              title="对账待确认"
              value={cap(pendingSettle.data?.items.length ?? 0, STAT_SIZE)}
              suffix="期"
              loading={pendingSettle.isPending}
            />
          </Col>
        </Row>
      </Card>

      <Card className="page-card" title="资质到期提醒">
        {supplier.isError ? (
          <Alert
            type="error"
            showIcon
            message="资质加载失败"
            description={supplier.error instanceof ApiError ? supplier.error.friendlyMessage : String(supplier.error)}
          />
        ) : null}
        <Table<QualificationRow>
          rowKey="key"
          size="middle"
          columns={columns}
          dataSource={quals}
          loading={supplier.isPending}
          pagination={false}
          locale={{ emptyText: '暂无资质备案' }}
        />
      </Card>
    </>
  );
}
